/** Per-renderer asynchronous ownership. load(key,signal) returns {value,dispose}.
 * Requests share in-flight work; successful resources persist until dispose.
 * Failed requests are retried only by a later explicit ensure call. Cancellation
 * rejects immediately and late decoders dispose their output without publishing. */
export function createSkyAssetCache({ load, valid = () => true, signal }) {
  const records = new Map(),
    values = {};
  let dead = false;
  const cancelled = () => new DOMException('Sky artwork loading cancelled', 'AbortError');
  function ensureKey(key) {
    if (dead || !valid()) return Promise.reject(cancelled());
    const prior = records.get(key);
    if (prior?.status === 'ready') return Promise.resolve(prior.value);
    if (prior?.status === 'loading') return prior.promise;
    const controller = new AbortController();
    const record = { status: 'loading', controller, error: null };
    records.set(key, record);
    const aborted = new Promise((resolve, reject) => {
      controller.signal.addEventListener('abort', () => reject(cancelled()), { once: true });
    });
    const work = Promise.resolve()
      .then(() => load(key, controller.signal))
      .then((asset) => {
        if (dead || !valid() || controller.signal.aborted) {
          asset.dispose();
          throw cancelled();
        }
        record.asset = asset;
        record.value = asset.value;
        values[key] = asset.value;
        record.status = 'ready';
        return asset.value;
      });
    record.promise = Promise.race([work, aborted]).catch((error) => {
      record.status = 'error';
      record.error = error;
      throw error;
    });
    return record.promise;
  }
  function ensure(keys) {
    return Promise.all([...new Set(keys)].map(ensureKey)).then(() => undefined);
  }
  function readiness(keys) {
    const entries = [...new Set(keys)].map((key) => records.get(key));
    const failed = entries.find((entry) => entry?.status === 'error');
    if (dead) return { status: 'error', error: cancelled() };
    if (failed) return { status: 'error', error: failed.error };
    if (entries.every((entry) => entry?.status === 'ready')) return { status: 'ready' };
    return { status: entries.some((entry) => entry?.status === 'loading') ? 'loading' : 'idle' };
  }
  function dispose() {
    if (dead) return;
    dead = true;
    signal?.removeEventListener('abort', dispose);
    for (const record of records.values()) {
      if (record.status === 'loading') record.controller.abort();
      record.asset?.dispose();
    }
    for (const key of Object.keys(values)) delete values[key];
  }
  signal?.addEventListener('abort', dispose, { once: true });
  if (signal?.aborted) dispose();
  return { values, ensure, readiness, dispose };
}
